import React, { useState } from 'react';
import type { Card } from "../../gameboardTypes.ts";
import StaticCard from '../../staticCard.tsx';
import { BASE_HEIGHT, BASE_WIDTH, BASE_WINDOW_WIDTH, PlayingCard } from '../../cardComponent.tsx';
import useWindowDimensions from '../../../../util/windowUtils.ts';
import { createText } from '../../../../util/cardEffects/cardText.ts';
import Clickable from '../../Clickable.tsx';
import { cardHasEffectType } from '../../../../util/cardEffects/effectProcessing.tsx';

interface DeckSelectorProps {
    cards: Card[],
    onSubmit: (cardId: string) => void,
    title?: string,
}

export default function CardListSelector({ cards, onSubmit, title }: DeckSelectorProps) {
    const { width } = useWindowDimensions();
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const cardWidth = BASE_WIDTH * (width / BASE_WINDOW_WIDTH);
    const containerPadding = 24;
    const cardSpacing = 16;
    const maxVisibleCards = 5;
    const scrollContainerWidth = Math.min(
        cards.length * (cardWidth + cardSpacing),
        maxVisibleCards * (cardWidth + cardSpacing) + cardSpacing
    );

    const handleSubmit = () => {
        if (selectedId) {
            onSubmit(selectedId);
        }
    };

    const cardElements = [];

    for (let j = 0; j < cards.length; j++) {
        const card = cards[j];
        const count = cards.filter(c => c.name === card.name).length;
        const isSelected = selectedId === card.id;

        const cardstack = [];
        for (let i = 0; i < count; i++) {
            cardstack.push(
                <StaticCard
                    rotation={0}
                    X={0}
                    Y={10 * i}
                >
                    <div
                        style={{
                            boxShadow: isSelected && i === count - 1
                                ? '0 0 0 4px #4ade80, 0 4px 12px rgba(0,0,0,0.3)'
                                : '0 2px 8px rgba(0,0,0,0.2)',
                            borderRadius: i === count - 1 ? '8px' : "",
                        }}
                    >
                        <PlayingCard
                            name={card?.name}
                            cost={card?.cost}
                            castThreshold={card?.castThreshold}
                            focus={card?.focus?.count}
                            tempFocus={card?.focus?.temp_count}
                            school={card?.school ?? "Manifest"}
                            classCard={card?.classCard}
                            ritual={cardHasEffectType("ritual", card)}
                            play={createText(card.playAbility)}
                            cast={createText(card.castAbility)}
                            sustain={createText(card.sustainAbility)}
                            passive={createText(card.passiveAbility)}
                            verticalIndex={(j * 4) + i}
                        />
                    </div>
                </StaticCard>
            )
        }

        cardElements.push(
            <div
                key={`deck-selector-${card.id}`}
                onClick={() => setSelectedId(card.id)}
                className='position-relative'
                style={{
                    cursor: 'pointer',
                    flexShrink: 0,
                    height: `${(1 * BASE_HEIGHT * (width / BASE_WINDOW_WIDTH)) + 30}px`,
                    width: `${BASE_WIDTH * (width / BASE_WINDOW_WIDTH)}px`,
                    marginTop: '30px',
                    transition: 'transform 0.15s ease',
                }}
            >
                {cardstack}
            </div>
        );
        j += count - 1;
    };

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
        >
            <div
                style={{
                    transform: `scale(.75)`,
                    backgroundColor: '#1f2937',
                    borderRadius: '12px',
                    padding: containerPadding,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    maxWidth: '90vw',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {title && (
                    <h2
                        style={{
                            margin: 0,
                            color: '#f3f4f6',
                            fontSize: '1.25rem',
                            fontWeight: 600,
                            textAlign: 'center',
                        }}
                    >
                        {title}
                    </h2>
                )}

                <div style={{ overflowX: `hidden` }}>
                    <div
                        style={{
                            display: 'flex',
                            gap: `${cardSpacing}px`,
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            padding: '8px 4px',
                            minWidth: `${scrollContainerWidth}px`,
                            maxWidth: 'calc(90vw - 48px)',
                            scrollbarWidth: 'thin',
                            scrollbarColor: '#4b5563 #1f2937',
                        }}
                    >
                        {cardElements}
                    </div>
                </div>
                <div
                    style={{
                        position: 'relative',
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center',
                    }}
                >
                    <Clickable
                        clickable={selectedId !== null}
                        onClick={handleSubmit}
                    >
                        <button
                            disabled={selectedId === null}
                            style={{
                                padding: '10px 20px',
                                borderRadius: '6px',
                                border: 'none',
                                backgroundColor: selectedId !== null ? '#4ade80' : '#374151',
                                color: selectedId !== null ? '#064e3b' : '#9ca3af',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: selectedId !== null ? 'pointer' : 'not-allowed',
                            }}
                        >
                            Submit
                        </button>
                    </Clickable>
                </div>
            </div>
        </div>
    );
}